import type { Token } from "marked";
import { marked } from "marked";
import React from "react";
import { ScrollView, Spacer, XStack, YStack } from "tamagui";

import Image from "@/components/Image";
import { captureMessage } from "@/sentry";
import { FontSizeTokens, lineHeight } from "@/theme/fonts";
import { Separator } from "./Layout";
import { ListBullet } from "./ListBullet";
import { H1, H2, H3, H4, H5, Link, Paragraph, Text } from "./Typography";

const decodeHtmlEntities = (text: string): string =>
  text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

// Create a type for Tamagui size tokens with $ prefix
type TamaguiSizeTokens = `$${FontSizeTokens}`;

const LIST_LINE_HEIGHT = 21;

const SUPPORTED_TOKENS = {
  SPACE: "space",
  HEADING: "heading",
  BR: "br",
  HR: "hr",
  BLOCKQUOTE: "blockquote",
  PARAGRAPH: "paragraph",
  TEXT: "text",
  LINK: "link",
  STRONG: "strong",
  EM: "em",
  DEL: "del",
  IMAGE: "image",
  LIST: "list",
  LIST_ITEM: "list_item",
  TABLE: "table",
  CODE: "code",
  CODESPAN: "codespan",
} as const;

// Overload marked.Parser to return array of React nodes instead of a string
type ExtendedToken = Token & {
  tokens?: Token[]; // Add this back since we need it for cell content
  depth?: number;
  items?: { tokens: Token[] }[];
  header?: Token[];
  rows?: Token[][];
  align?: ("left" | "center" | "right" | null)[];
  start?: number;
  ordered?: boolean;
};

interface MarkdownImageProps {
  href: string;
  alt: string;
  lastIsNewLine: boolean;
}

const MarkdownImage = ({ href, alt, lastIsNewLine }: MarkdownImageProps) => {
  const [loadError, setLoadError] = React.useState(false);

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  if (loadError || !isValidUrl(href)) {
    return (
      <Paragraph
        marginTop={!lastIsNewLine ? "$100" : 0}
        color="$textSecondary"
        fontSize="$sm"
        fontFamily="$serif"
        italic
      >
        Could not display image
      </Paragraph>
    );
  }

  return (
    <YStack marginTop={!lastIsNewLine ? "$100" : 0} marginBottom="$100">
      <Image
        source={{ uri: href }}
        alt={alt}
        width="100%"
        maxWidth={300}
        height={300}
        objectFit="contain"
        onError={({ error }) => {
          setLoadError(true);
          captureMessage(`Image load failed: ${href}`, {
            extra: { error },
          });
        }}
      />
    </YStack>
  );
};

class MyParser {
  private listLineHeight: number | undefined = undefined;

  constructor(private size: FontSizeTokens | undefined) {
    this.size = size;
  }

  private isValidHref(href: string): boolean {
    if (!href) return false;

    const trimmedHref = href.trim().toLowerCase();
    const dangerousProtocols = ["javascript:", "data:", "vbscript:", "file:"];

    return !dangerousProtocols.some((protocol) =>
      trimmedHref.startsWith(protocol),
    );
  }

  parse(tokens: Token[] | Token, alreadyInText = false): React.ReactNode[] {
    const baseFontProps = this.size
      ? { size: `$${this.size}` as TamaguiSizeTokens }
      : { conversational: true };
    const fontProps =
      this.listLineHeight !== undefined
        ? { ...baseFontProps, lineHeight: this.listLineHeight }
        : baseFontProps;
    const listBulletMarginVertical = this.size
      ? `${lineHeight[this.size] / 2}px`
      : "$100";
    const out: React.ReactNode[] = [];

    const tokenArray = Array.isArray(tokens) ? tokens : [tokens];
    tokenArray.forEach((token) => {
      const lastIsNewLine = out.length === 0 || out[out.length - 1] === "\n";
      switch (token.type) {
        case SUPPORTED_TOKENS.SPACE: {
          if (!lastIsNewLine) out.push(<Spacer size="$150" />);
          break;
        }
        case SUPPORTED_TOKENS.HEADING: {
          const headingComponents = [H1, H2, H3, H4, H5];
          const depth = (token as ExtendedToken).depth || 1;
          const HeadingComponent = headingComponents[depth - 1] || H5;

          out.push(
            <HeadingComponent fontFamily="$serif">
              {this.parse(token.tokens as Token[], true)}
            </HeadingComponent>,
          );
          break;
        }
        case SUPPORTED_TOKENS.BR: {
          if (alreadyInText) out.push("\n");
          break;
        }
        case SUPPORTED_TOKENS.HR: {
          out.push(<Separator />);
          break;
        }
        case SUPPORTED_TOKENS.BLOCKQUOTE: {
          out.push(
            <YStack
              borderLeftWidth={4}
              borderLeftColor="$textSecondary"
              paddingLeft="$200"
              marginVertical="$100"
            >
              {this.parse(token.tokens as Token[])}
            </YStack>,
          );
          break;
        }
        case SUPPORTED_TOKENS.PARAGRAPH: {
          out.push(
            <Paragraph color="$textPrimary" {...fontProps}>
              {this.parse(token.tokens as Token[], true)}
            </Paragraph>,
          );
          break;
        }
        case SUPPORTED_TOKENS.TEXT: {
          const children = token.tokens
            ? this.parse(token.tokens, true)
            : token.text;
          out.push(
            alreadyInText ? (
              children
            ) : (
              <Text whiteSpace="pre-wrap" {...fontProps}>
                {children}
              </Text>
            ),
          );
          break;
        }
        case SUPPORTED_TOKENS.LINK: {
          if (this.isValidHref(token.href)) {
            out.push(
              <Link
                href={token.href}
                dataTrackingId="markdown-link"
                external
                {...fontProps}
              >
                {token.text}
              </Link>,
            );
          } else {
            out.push(
              <Text whiteSpace="pre-wrap" {...fontProps}>
                {token.text}
              </Text>,
            );
          }
          break;
        }
        case SUPPORTED_TOKENS.STRONG: {
          out.push(
            <Text whiteSpace="pre-wrap" {...fontProps} bold>
              {this.parse(token.tokens as Token[], true)}
            </Text>,
          );
          break;
        }
        case SUPPORTED_TOKENS.EM: {
          out.push(
            <Text whiteSpace="pre-wrap" {...fontProps} fontStyle="italic">
              {this.parse(token.tokens as Token[], true)}
            </Text>,
          );
          break;
        }
        case SUPPORTED_TOKENS.DEL: {
          out.push(
            <Text
              whiteSpace="pre-wrap"
              textDecorationLine="line-through"
              {...fontProps}
            >
              {this.parse(token.tokens as Token[], true)}
            </Text>,
          );
          break;
        }
        case SUPPORTED_TOKENS.IMAGE: {
          out.push(
            <MarkdownImage
              href={token.href}
              alt={token.text || ""}
              lastIsNewLine={lastIsNewLine}
            />,
          );
          break;
        }

        case SUPPORTED_TOKENS.LIST: {
          if (token.items) {
            const prevListLineHeight = this.listLineHeight;
            if (!this.size) this.listLineHeight = LIST_LINE_HEIGHT;
            const numberFontProps =
              this.listLineHeight !== undefined
                ? { ...fontProps, lineHeight: this.listLineHeight }
                : fontProps;
            const items = token.items?.map(
              ({ tokens }: { tokens: Token[] }, i: number) => (
                // We don't use BulletedList because there may be nested lists that can't go inside Text on native

                <XStack alignItems="flex-start" gap="$100" key={i}>
                  {token.ordered ? (
                    <XStack minWidth="$250" justifyContent="flex-end">
                      <Text
                        {...numberFontProps}
                      >{`${i + ((token as ExtendedToken).start || 1)}. `}</Text>
                    </XStack>
                  ) : (
                    <XStack minWidth="$250" justifyContent="center">
                      <ListBullet
                        alignSelf="flex-end"
                        marginVertical={listBulletMarginVertical}
                      />
                    </XStack>
                  )}
                  <YStack flexShrink={1}>{this.parse(tokens)}</YStack>
                </XStack>
              ),
            );
            this.listLineHeight = prevListLineHeight;
            out.push(
              <YStack
                maxWidth="100%"
                gap="$25"
                marginTop={!lastIsNewLine ? "$50" : 0}
              >
                {items}
              </YStack>,
            );
          }
          break;
        }
        case SUPPORTED_TOKENS.LIST_ITEM: {
          out.push(
            <XStack alignItems="flex-start" gap="$100">
              <ListBullet marginVertical={listBulletMarginVertical} />
              <YStack flexShrink={1}>{this.parse(token)}</YStack>
            </XStack>,
          );
          break;
        }
        case SUPPORTED_TOKENS.CODE: {
          out.push(
            <ScrollView horizontal>
              <YStack
                backgroundColor="$fillNeutral"
                borderRadius="$100"
                padding="$200"
                marginTop={!lastIsNewLine ? "$100" : 0}
                width="max-content"
              >
                <Text fontFamily="$monospace" fontSize="$sm" whiteSpace="pre">
                  {decodeHtmlEntities(token.text)}
                </Text>
              </YStack>
            </ScrollView>,
          );
          break;
        }
        case SUPPORTED_TOKENS.CODESPAN: {
          out.push(
            <Text
              fontFamily="$monospace"
              fontSize="$sm"
              backgroundColor="$fillNeutral"
              borderRadius={4}
              paddingHorizontal="$25"
              {...fontProps}
            >
              {decodeHtmlEntities(token.text)}
            </Text>,
          );
          break;
        }
        case SUPPORTED_TOKENS.TABLE: {
          const header = token.header || [];
          const rows = token.rows || [];
          const aligns = token.align || [];
          const columnCount = header.length;
          const columnWidth = `${100 / columnCount}%`;

          out.push(
            <ScrollView horizontal>
              <YStack width="max-content" overflow="hidden">
                {/* Header */}
                <XStack
                  borderBottomColor="$borderColor"
                  borderBottomWidth={1}
                  style={{ whiteSpace: "nowrap" }}
                  flexWrap="nowrap"
                >
                  {header.map((cell: Token, cellIndex: number) => {
                    const headerContent =
                      typeof cell === "string"
                        ? cell
                        : this.parse(
                            (cell as ExtendedToken).tokens || [],
                            true,
                          );
                    return (
                      <XStack
                        key={cellIndex}
                        flex={1}
                        minWidth={columnWidth}
                        maxWidth={columnWidth}
                        padding="$100"
                        paddingLeft={cellIndex === 0 ? 0 : undefined}
                      >
                        <Text
                          fontFamily="$serif"
                          flex={1}
                          bold
                          textAlign={aligns[cellIndex] || "left"}
                        >
                          {headerContent}
                        </Text>
                      </XStack>
                    );
                  })}
                </XStack>
                {/* Rows */}
                {rows.map((row: Token[], rowIndex: number) => (
                  <XStack
                    key={rowIndex}
                    borderBottomWidth={rowIndex < rows.length - 1 ? 1 : 0}
                    borderBottomColor="$borderColor"
                  >
                    {row.map((cell: Token, cellIndex: number) => {
                      const cellContent =
                        typeof cell === "string"
                          ? cell
                          : this.parse(
                              (cell as ExtendedToken).tokens || [],
                              true,
                            );
                      return (
                        <XStack
                          key={cellIndex}
                          flex={1}
                          padding="$200"
                          paddingLeft={cellIndex === 0 ? 0 : undefined}
                          minWidth={columnWidth}
                          maxWidth={columnWidth}
                        >
                          <Text
                            fontFamily="$serif"
                            flex={1}
                            textAlign={aligns[cellIndex] || "left"}
                            {...fontProps}
                          >
                            {cellContent}
                          </Text>
                        </XStack>
                      );
                    })}
                  </XStack>
                ))}
              </YStack>
            </ScrollView>,
          );
          break;
        }
        default: {
          captureMessage(
            `Unsupported token type ${token.type} found while parsing markdown.`,
          );
          break;
        }
      }
    });
    return out.map((node, i) => (
      <React.Fragment key={i}>{node}</React.Fragment>
    ));
  }
}

interface FormattedMarkdownProps {
  markdown: string;
  size?: FontSizeTokens;
}

const FormattedMarkdownComponent = ({
  markdown,
  size,
}: FormattedMarkdownProps) => {
  const nodes = React.useMemo(() => {
    const tokens = marked.lexer(markdown);
    const myParser = new MyParser(size);
    return myParser.parse(tokens);
  }, [markdown, size]);

  return nodes;
};

FormattedMarkdownComponent.displayName = "FormattedMarkdown";

export const FormattedMarkdown = React.memo(FormattedMarkdownComponent);
